import '@theme/dialog';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { replaceSection, updateCartCount } from '@theme/section-renderer';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('change', this.onQuantityChange, { signal });
    this.addEventListener('click', this.onRemoveClick, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.requestController?.abort();
  }

  onQuantityChange = (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!(input instanceof HTMLInputElement)) return;
    this.changeLine(Number(input.dataset.cartLine), Number(input.value), input);
  };

  onRemoveClick = (event) => {
    const link = event.target.closest('[data-cart-remove]');
    if (!(link instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    this.changeLine(Number(link.dataset.cartLine), 0, link);
  };

  async changeLine(line, quantity, source) {
    if (!line || Number.isNaN(quantity)) return;
    this.requestController?.abort();
    this.requestController = new AbortController();
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: quantity === 0 ? 'remove' : 'update',
      context: 'cart',
      lines: [{ merchandiseId: source.dataset.variantId || '', quantity }],
      promise: deferred.promise,
    }));

    this.setAttribute('aria-busy', 'true');
    const currentLine = source.closest('[data-cart-line-key]');
    const currentKey = currentLine?.dataset.cartLineKey || '';
    const nextKey = currentLine?.nextElementSibling?.dataset.cartLineKey || currentLine?.previousElementSibling?.dataset.cartLineKey || '';
    if (source instanceof HTMLInputElement) source.disabled = true;
    try {
      const response = await fetch(document.documentElement.dataset.cartChangeUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }),
        signal: this.requestController.signal,
      });
      const cart = await response.json();
      if (!response.ok || cart.errors) throw new Error(cart.description || cart.message || cart.errors);

      if (cart.sections?.['cart-drawer']) replaceSection('cart-drawer', cart.sections['cart-drawer'], { mode: 'hydration' });
      updateCartCount(cart.item_count);
      const nextDrawer = document.querySelector('cart-drawer');
      const status = nextDrawer?.querySelector('[data-cart-status]');
      if (status) status.textContent = quantity === 0 ? this.dataset.removedMessage : this.dataset.updatedMessage;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, source: 'cart-drawer', sourceId: String(line), itemCount: quantity },
      });

      const focusSelector = quantity > 0 && currentKey
        ? `[data-cart-line-key="${CSS.escape(currentKey)}"] [data-cart-quantity]`
        : nextKey
          ? `[data-cart-line-key="${CSS.escape(nextKey)}"] [data-cart-focus-fallback]`
          : '[data-cart-empty] a';
      document.querySelector(focusSelector)?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      const message = error.message || this.dataset.errorMessage;
      this.querySelector('[data-cart-status]')?.replaceChildren(message);
      this.dispatchEvent(new CartErrorEvent({
        error: message,
        code: 'INVALID',
        detail: { description: message, errors: message },
      }));
      deferred.reject(error);
    } finally {
      this.removeAttribute('aria-busy');
      if (source instanceof HTMLInputElement && source.isConnected) source.disabled = false;
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
